import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Supported locales for multi-language fields
const LOCALES = ["en", "fr", "nl"] as const;

type Entity = "questions" | "survey-templates";

interface CSVImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entity: Entity;
  onSuccess?: () => void;
}

const buildQuestionHeaders = () => {
  const base = [
    "id",
    // foreign keys
    "tool_id",
    "tool_name",
    "question_type_id",
    "question_type_name",
    // attributes
    "is_required",
    "order_index",
    // relationships
    "focus_area_names",
    "survey_template_id",
    "survey_template_name",
    "template_order_index",
  ];
  const i18n = LOCALES.map((l) => `question_text_${l}`);
  return [...base.slice(0, 1), ...i18n, ...base.slice(1)];
};

const buildTemplateHeaders = () => {
  const base = [
    "id",
    // attributes
    "name",
    "description",
    "category",
    "status",
    "is_featured",
    "estimated_duration_minutes",
    "target_audience",
    // foreign keys
    "tool_id",
    "tool_name",
  ];
  // Optional i18n
  const i18n = [
    ...LOCALES.map((l) => `name_${l}`),
    ...LOCALES.map((l) => `description_${l}`),
  ];
  return [...base, ...i18n];
};

const Docs = ({ entity }: { entity: Entity }) => {
  if (entity === "questions") {
    const headers = buildQuestionHeaders();
    return (
      <div className="space-y-3 text-sm">
        <p>Columns and expected values for Questions CSV:</p>
        <ul className="list-disc pl-5 space-y-1">
          {headers.map((h) => (
            <li key={h}><code>{h}</code></li>
          ))}
        </ul>
        <Separator />
        <ul className="list-disc pl-5 space-y-1">
          <li><code>id</code>: Optional. If provided, the row will update the existing question; otherwise a new one will be created.</li>
          <li><code>question_text_{"{lang}"}</code>: Multi-language question text. Supported langs: {LOCALES.join(", ")}.</li>
          <li><code>is_required</code>: true/false.</li>
          <li><code>order_index</code>: Integer.</li>
          <li><code>tool_id</code> or <code>tool_name</code>: One of them is required. We validate it exists.</li>
          <li><code>question_type_id</code> or <code>question_type_name</code>: One of them is required. We validate it exists.</li>
          <li><code>focus_area_names</code>: Comma-separated list of existing Focus Area names. Example: "Engagement, Leadership".</li>
          <li><code>survey_template_id</code> or <code>survey_template_name</code>: Optional. If provided, links the question to that template. Use <code>template_order_index</code> to control order.</li>
        </ul>
      </div>
    );
  }

  const headers = buildTemplateHeaders();
  return (
    <div className="space-y-3 text-sm">
      <p>Columns and expected values for Survey Templates CSV:</p>
      <ul className="list-disc pl-5 space-y-1">
        {headers.map((h) => (
          <li key={h}><code>{h}</code></li>
        ))}
      </ul>
      <Separator />
      <ul className="list-disc pl-5 space-y-1">
        <li><code>id</code>: Optional. If provided, updates the existing template; otherwise creates a new one.</li>
        <li><code>name</code>/<code>description</code>: Base values. You can also provide localized versions using <code>name_{"{lang}"}</code> and <code>description_{"{lang}"}</code> for {LOCALES.join(", ")}.</li>
        <li><code>category</code>: Text tag like "general".</li>
        <li><code>status</code>: draft/published (text).</li>
        <li><code>is_featured</code>: true/false.</li>
        <li><code>estimated_duration_minutes</code>: Integer.</li>
        <li><code>target_audience</code>: Free text.</li>
        <li><code>tool_id</code> or <code>tool_name</code>: One of them is required.</li>
      </ul>
    </div>
  );
};

export const CSVImportDialog = ({ isOpen, onClose, entity, onSuccess }: CSVImportDialogProps) => {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<null | { createCount: number; updateCount: number; errors: string[] }>(null);
  const [busy, setBusy] = useState(false);

  const headers = useMemo(() => entity === "questions" ? buildQuestionHeaders() : buildTemplateHeaders(), [entity]);

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: headers, data: [] });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entity}-import-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreview(null);
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const parseAndPreview = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const rows = await new Promise<any[]>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (r) => resolve(r.data as any[]),
          error: (err) => reject(err),
        });
      });

      const fn = entity === "questions" ? "import-questions" : "import-templates";
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { dryRun: true, rows },
      });
      if (error) throw error;

      setPreview({
        createCount: data?.createCount || 0,
        updateCount: data?.updateCount || 0,
        errors: data?.errors || [],
      });
      if (!data?.errors?.length) {
        toast({ title: "Validation successful", description: "No blocking issues found." });
      } else {
        toast({ title: "Validation completed with issues", description: `${data.errors.length} problem(s) detected`, variant: "destructive" });
      }
    } catch (e: any) {
      console.error(e);
      toast({ title: "Parsing/validation failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setParsing(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const rows = await new Promise<any[]>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (r) => resolve(r.data as any[]),
          error: (err) => reject(err),
        });
      });

      const fn = entity === "questions" ? "import-questions" : "import-templates";
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { dryRun: false, rows },
      });
      if (error) throw error;

      toast({ title: "Import completed", description: `${data?.created || 0} created, ${data?.updated || 0} updated` });
      onSuccess?.();
      onClose();
    } catch (e: any) {
      console.error(e);
      toast({ title: "Import failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o ? onClose() : null}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import {entity === "questions" ? "Questions" : "Survey Templates"}</DialogTitle>
          <DialogDescription>Upload a CSV, preview validation, then import.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="template">Download Template</TabsTrigger>
            <TabsTrigger value="docs">Docs</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>CSV File</Label>
              <Input type="file" accept=".csv" onChange={handleFileChange} />
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={parseAndPreview} disabled={!file || parsing}>
                {parsing ? "Validating..." : "Preview Validation"}
              </Button>
              <Button onClick={runImport} disabled={!file || busy}>
                {busy ? "Importing..." : "Import"}
              </Button>
            </div>
            {preview && (
              <div className="rounded-md border p-3 text-sm">
                <div className="font-medium mb-2">Preview</div>
                <div className="flex gap-6">
                  <div>To create: <span className="font-semibold">{preview.createCount}</span></div>
                  <div>To update: <span className="font-semibold">{preview.updateCount}</span></div>
                </div>
                {preview.errors.length > 0 && (
                  <div className="mt-2 text-destructive">
                    {preview.errors.slice(0, 10).map((e, i) => (
                      <div key={i}>• {e}</div>
                    ))}
                    {preview.errors.length > 10 && (
                      <div>…and {preview.errors.length - 10} more</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="template" className="space-y-4 mt-4">
            <p className="text-sm">Download a blank CSV template with the correct headers.</p>
            <Button onClick={downloadTemplate}>Download CSV Template</Button>
          </TabsContent>

          <TabsContent value="docs" className="mt-4">
            <Docs entity={entity} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
